import { useRoute } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Bell,
  Check,
  CheckSquare,
  CopyPlus,
  GraduationCap,
  Lightbulb,
  MoreVertical,
  Move,
  PencilLine,
  Square,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import { Pressable } from "react-native";
import { type SheetProps, View, XStack, YStack } from "tamagui";
import { useApi } from "../../core/apis";
import { hasHelpData } from "../../core/screens/learndata/utils";
import { useStores } from "../../core/stores";
import { doResetHelperStore } from "../../helper/HelperManager";
import type BaseModuleModel from "../../modules/core/BaseModuleModel";
import type {
  ModuleModelData,
  Snapshot,
} from "../../modules/core/BaseModuleModel";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { Body } from "../Body";
import { Button } from "../Button";
import { EmojiPicker } from "../EmojiPicker";
import { Group } from "../Group";
import { LabelTextarea } from "../Label";
import { ModuleIcon } from "../ModuleIcon";
import { PopoverConfirm } from "../PopoverConfirm";
import { Sheet } from "../Sheet";
import { Subtext } from "../Subtext";
import { TipsText } from "../TipsText";
import { Title } from "../Title";
import { BackButton } from "./BackButton";
import { ShareSheet } from "./ShareSheet";

const ModuleMoveSheet: React.FC<SheetProps & { moduleUid: string }> = observer(
  ({ moduleUid, ...props }) => {
    const { modulesStore } = useStores();
    const { sectionsApi } = useApi();

    const module = modulesStore.byId.get(moduleUid);

    React.useEffect(() => {
      if (props.open && module?.plan && !module.plan.isSectionsLoaded) {
        module.plan.data.sectionIds.map((sectionUid) => {
          return sectionsApi.getById(sectionUid);
        });
      }
    }, [module, sectionsApi, props.open]);

    if (!module) {
      return null;
    }

    return (
      <Sheet
        sheetId="move_module"
        {...props}
        header={
          <YStack gap="$medium">
            <XStack gap="$medium" alignItems="center">
              <Move />
              <Title>Move Card</Title>
            </XStack>
            <TipsText text="Select the group this card belongs to" />
          </YStack>
        }
      >
        <Group>
          {module.plan.sections.map((section) => {
            if (!section.data.isExpandable) {
              return null;
            }

            const isInSection = section.uid === module.data.sectionUid;

            if (isInSection) {
              return (
                <View key={section.uid} width={"100%"}>
                  <YStack gap="$medium" width={"100%"}>
                    <XStack
                      width={"100%"}
                      alignItems="center"
                      gap="$space.small"
                    >
                      {isInSection ? <CheckSquare /> : <Square />}
                      <Body>{section.data.title}</Body>
                    </XStack>
                  </YStack>
                </View>
              );
            }

            return (
              <XStack
                width={"100%"}
                key={section.uid}
                alignItems="center"
                gap="$space.small"
                onPress={() => {
                  module.plan.moveModule(module.uid, section.uid);
                  props.onOpenChange?.(false);
                }}
                cursor="pointer"
              >
                <Square />
                <Body>{section.data.title}</Body>
              </XStack>
            );
          })}
        </Group>
      </Sheet>
    );
  },
);

export const ModuleArchiveSheet: React.FC<SheetProps & { moduleUid: string }> =
  observer(({ moduleUid, ...props }) => {
    const { modulesStore } = useStores();

    const module = modulesStore.byId.get(moduleUid);

    if (!module) {
      return null;
    }

    return (
      <Sheet sheetId="archive_module" {...props}>
        <YStack gap="$medium">
          <XStack gap="$small">
            <Trash2 size={"$size.normal"} color={"$muted"} />
            <Title>Confirm action</Title>
          </XStack>

          <Body>Are you sure you want to delete this step?</Body>
          <Subtext>
            You can undo this within Planira settings -&gt; Undo delete later on
            if needed
          </Subtext>

          <Group>
            <XStack
              width={"100%"}
              alignItems="center"
              gap="$space.small"
              onPress={() => {
                module.plan.archiveModule(moduleUid);
                props.onOpenChange?.(false);
              }}
              cursor="pointer"
            >
              <ThumbsUp />
              <Body>Yes please!</Body>
            </XStack>
            <XStack
              width={"100%"}
              alignItems="center"
              gap="$space.small"
              onPress={() => {
                props.onOpenChange?.(false);
              }}
              cursor="pointer"
            >
              <ThumbsDown />
              <Body>No, not right now</Body>
            </XStack>
          </Group>
        </YStack>
      </Sheet>
    );
  });

export const useSharedModuleMenuOptions = ({
  module,
  onHideSheet,
  skipModuleOptions: skipModuleMenuOptions,
}: {
  module?: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>;
  onHideSheet: () => void;
  skipModuleOptions?: boolean;
}) => {
  const navigation = usePlanNavigation();

  if (!module) {
    return null;
  }

  const hasLearnMoreData = hasHelpData(module.data.type);
  const isQuickstart = module.plan.isQuickstart;

  const menuItems = [];

  !skipModuleMenuOptions &&
    module.renderMenuOptions().forEach((menuOption, _i) => {
      const Icon = menuOption.icon;

      menuItems.push(
        <XStack
          key={`menu_${menuOption.label}`}
          width={"100%"}
          alignItems="center"
          gap="$space.small"
          onPress={() => {
            menuOption.onPress();
            onHideSheet();
          }}
          cursor="pointer"
        >
          <Icon />
          <Body>{menuOption.label}</Body>
          {menuOption.checked && <Check />}
        </XStack>,
      );
    });

  if (module.hasHelperGuide) {
    menuItems.push(
      <XStack
        key={"helper-guide"}
        width={"100%"}
        alignItems="center"
        gap="$space.small"
        onPress={() => {
          doResetHelperStore(module.uid);
          onHideSheet();
        }}
        cursor="pointer"
      >
        <Lightbulb />
        <Body>Show guide</Body>
      </XStack>,
    );
  }

  if (!isQuickstart) {
    menuItems.push(
      <XStack
        key={"muted-setting"}
        width={"100%"}
        alignItems="center"
        gap="$space.small"
        onPress={() => {
          module.setMuted(!module.data.isMuted);
          onHideSheet();
        }}
        cursor="pointer"
      >
        <Bell />
        <Body>{module.data.isMuted ? "Unmute updates" : "Mute updates"}</Body>
      </XStack>,
    );
  }

  if (hasLearnMoreData) {
    menuItems.push(
      <XStack
        key={"learn-more"}
        width={"100%"}
        alignItems="center"
        gap="$space.small"
        onPress={() => {
          onHideSheet();

          navigation.navigate("Learn", {
            planUid: module.plan.uid,
            card: module.data.type,
          });
        }}
        cursor="pointer"
      >
        <GraduationCap />
        <Body>Learn more</Body>
      </XStack>,
    );
  }

  return menuItems;
};

export const ModuleHeader: React.FC = observer(() => {
  const [showEmojiSheet, setSetEmojiSheet] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [showMoveSheet, setShowMoveSheet] = useState(false);
  const [showArchiveSheet, setShowArchiveSheet] = useState(false);
  const [autoFocusTitle, setFocusTitle] = useState(false);

  const route =
    useRoute<
      NativeStackScreenProps<ReactNavigation.RootParamList, "Module">["route"]
    >();
  const navigation = usePlanNavigation();
  const { modulesStore } = useStores();

  const goToFallback = useCallback(() => {
    if (route.params.dataUid) {
      navigation.replace("Module", {
        planUid: route.params.planUid,
        moduleUid: route.params.moduleUid,
      });
    } else {
      navigation.replace("Plan", { planUid: route.params.planUid });
    }
  }, [navigation, route]);

  const module = modulesStore.byId.get(route.params.moduleUid);
  const showMenuButton = !route.params.dataUid;

  const title = module?.data.title ?? "";

  const icon = module ? <ModuleIcon module={module} /> : <View />;

  useEffect(() => {
    if (module?.data.title) {
      navigation.setOptions({ title: `${module.data.title} - Planira` });
    }
  }, [module?.data.title, navigation]);

  const sharedMenuItems = useSharedModuleMenuOptions({
    module,
    onHideSheet: () => {
      setShowSheet(false);
    },
    skipModuleOptions: module?.isStatic,
  });

  const toast = useToastController();

  if (!module?.data) {
    return null;
  }

  return (
    <>
      <EmojiPicker
        sheetId="emoji"
        open={showEmojiSheet}
        onOpenChange={(open: boolean) => setSetEmojiSheet(open)}
        onSelect={(emoji) => {
          module?.setEmoji(emoji.emoji);
          setSetEmojiSheet(false);
        }}
      />
      <View backgroundColor={"$backgroundBlock"}>
        <XStack
          alignItems="stretch"
          gap="$space.small"
          width="100%"
          justifyContent="space-between"
        >
          <XStack
            flex={1}
            padding="$space.large"
            gap="$space.small"
            alignItems="center"
          >
            <BackButton goToFallback={goToFallback} />
            <Pressable onPress={() => setSetEmojiSheet(true)}>{icon}</Pressable>
            <LabelTextarea
              key={`render${autoFocusTitle}`}
              autoFocus={autoFocusTitle}
              blurOnSubmit
              placeholder="Set title..."
              multiline={false}
              numberOfLines={1}
              textOverflow="ellipsis"
              defaultValue={title}
              onSubmitEditing={(e) => {
                module?.setTitle(e.nativeEvent.text);
                setFocusTitle(false);
              }}
              submitOnEnter
            />
          </XStack>

          {showMenuButton && (
            <XStack alignItems="stretch">
              <Button onPress={() => setShowSheet(true)} icon={MoreVertical} />
            </XStack>
          )}
        </XStack>
      </View>
      {module?.plan && (
        <>
          <ShareSheet planUid={module.plan.uid} />
          <Sheet
            sheetId="settings_module"
            open={showSheet}
            onOpenChange={setShowSheet}
            header={<Title>Settings</Title>}
          >
            <YStack gap="$medium">
              <Group borderTop={false}>
                <XStack
                  width={"100%"}
                  alignItems="center"
                  gap="$space.small"
                  onPress={() => {
                    setFocusTitle(true);
                    setShowSheet(false);
                  }}
                  cursor="pointer"
                >
                  <PencilLine />
                  <Body>Rename</Body>
                </XStack>

                {sharedMenuItems}

                {module?.data.isDraggable &&
                  module.plan.data.settings.useSections && (
                    <XStack
                      width={"100%"}
                      alignItems="center"
                      gap="$space.small"
                      onPress={() => {
                        setShowMoveSheet(true);
                        setShowSheet(false);
                      }}
                      cursor="pointer"
                    >
                      <Move />
                      <Body>Move</Body>
                    </XStack>
                  )}

                <PopoverConfirm
                  type={"success"}
                  confirmTitle={"Copy this step?"}
                  confirmDescription={"Step will be copied and reset."}
                  onConfirm={async () => {
                    const _newModule = await module.copy();

                    // Navigate to overview for now
                    navigation.navigate("Plan", { planUid: module.plan.uid });

                    toast.show("Step copied!");
                  }}
                >
                  <XStack
                    width={"100%"}
                    alignItems="center"
                    gap="$space.small"
                    cursor="pointer"
                  >
                    <CopyPlus size={"$size.normal"} color={"$muted"} />
                    <Body>Copy</Body>
                  </XStack>
                </PopoverConfirm>

                {module?.data.isDraggable && (
                  <XStack
                    width={"100%"}
                    alignItems="center"
                    gap="$space.small"
                    onPress={() => {
                      setShowArchiveSheet(true);
                      setShowSheet(false);
                    }}
                    cursor="pointer"
                  >
                    <Trash2 size={"$size.normal"} color={"$muted"} />
                    <Body>Delete</Body>
                  </XStack>
                )}
              </Group>
            </YStack>
          </Sheet>

          <ModuleMoveSheet
            moduleUid={module.uid}
            open={showMoveSheet}
            onOpenChange={setShowMoveSheet}
          />

          <ModuleArchiveSheet
            moduleUid={module.uid}
            open={showArchiveSheet}
            onOpenChange={setShowArchiveSheet}
          />
        </>
      )}
    </>
  );
});
